import { createFrontendModule, PageBlueprint } from '@backstage/frontend-plugin-api';
import type { CatalogTableRow } from '@backstage/plugin-catalog';
import { catalogPlugin, CatalogTable } from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  EntityKindPicker,
} from '@backstage/plugin-catalog-react';
import {
  Content,
  ContentHeader,
  CreateButton,
  PageWithHeader,
  SupportButton,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import type { TableColumn } from '@backstage/core-components';
import { Link } from '@backstage/core-components';
import Typography from '@material-ui/core/Typography';
import CategoryIcon from '@material-ui/icons/Category';

/**
 * Picks the URL to show for an entity, from the standard `metadata.links`.
 *
 * No new field is introduced: `metadata.links` is Backstage's own place for
 * this and several entities already populate it.
 *
 * Entities carry more than one link -- atteniv-marketing has both a
 * production site and a Vercel deployment, and tax-nexus-web has the app and
 * its Swagger UI -- and the primary one is titled inconsistently across the
 * catalog ("Production", "Nexus Radar", "Chatwoot"). So the choice is made by
 * intent rather than by title:
 *
 *   1. a link explicitly titled Production
 *   2. otherwise the first link that is not a known secondary destination,
 *      which keeps a Swagger UI or a preview deployment from outranking the
 *      application itself
 *   3. otherwise the first link
 *
 * Libraries legitimately have no links and render an empty cell.
 */
const SECONDARY_LINK = /swagger|api explorer|developer|docs|repo|source|dashboard/i;

export function primaryLink(entity: {
  metadata?: { links?: Array<{ url: string; title?: string }> };
}) {
  const links = entity.metadata?.links ?? [];
  if (links.length === 0) return undefined;
  return (
    links.find(l => /^production$/i.test(l.title ?? '')) ??
    links.find(l => !SECONDARY_LINK.test(l.title ?? '')) ??
    links[0]
  );
}

/** Hostname plus path, without the scheme, so the cell stays narrow. */
export function displayUrl(url: string) {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}`;
  } catch {
    return url;
  }
}

/**
 * The URL column.
 *
 * `field` is set so material-table can sort on it, and search is widened to
 * match any of the entity's links rather than only the displayed one, so
 * searching for a Swagger URL still finds its component.
 */
/**
 * Description, wrapped rather than truncated.
 *
 * The stock column renders through `OverflowTooltip`, which clamps to a
 * single line and shows the rest only on hover:
 *
 *     overflow: hidden
 *     textOverflow: ellipsis
 *     display: -webkit-box
 *     -webkit-line-clamp: line || 1
 *
 * Descriptions here run to 202 characters with a median of 107, so almost
 * every row was being cut. `OverflowTooltip` takes a `line` prop, but any
 * fixed number still truncates the longest entries, so this renders plain
 * wrapping text instead and lets the row grow.
 *
 * `cellStyle` is needed as well as the renderer: material-table applies
 * `padding: dense` and its cells otherwise resist wrapping.
 */
const descriptionColumn: TableColumn<CatalogTableRow> = {
  ...CatalogTable.columns.createMetadataDescriptionColumn(),
  width: 'auto',
  cellStyle: { whiteSpace: 'normal', wordBreak: 'break-word' },
  render: ({ entity }: CatalogTableRow) => (
    <Typography
      variant="inherit"
      component="span"
      style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
    >
      {entity.metadata.description}
    </Typography>
  ),
};

const urlColumn: TableColumn<CatalogTableRow> = {
  title: 'URL',
  field: 'entity.metadata.links[0].url',
  width: '16%',
  customFilterAndSearch: (query, row) => {
    const haystack = (row.entity.metadata?.links ?? [])
      .map(l => `${l.title ?? ''} ${l.url}`)
      .join(' ')
      .toLocaleLowerCase('en-US');
    return haystack.includes(query.toLocaleLowerCase('en-US'));
  },
  customSort: (a, b) => {
    const av = primaryLink(a.entity)?.url ?? '';
    const bv = primaryLink(b.entity)?.url ?? '';
    return av.localeCompare(bv);
  },
  render: ({ entity }) => {
    const link = primaryLink(entity);
    if (!link) return null;
    return (
      <Link to={link.url} target="_blank" rel="noopener noreferrer" title={link.url}>
        {displayUrl(link.url)}
      </Link>
    );
  },
};

/**
 * Columns for the catalog table.
 *
 * Owner and Tags are deliberately absent. Every Atteniv component is owned by
 * `group:default/engineering`, so an Owner column repeats one value on every
 * row, and the tag lists are long enough to force rows several lines tall,
 * which squeezes Description into an ellipsis. Both remain on the entity page,
 * and tags are still matched by the table's search box.
 *
 * Widths are percentages on the fixed columns so Description, left at `auto`,
 * absorbs the remainder.
 */
const columns = [
  // Sorting falls back to this when a title is present; Name renders it.
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  { ...CatalogTable.columns.createNameColumn({ defaultKind: 'Component' }), width: '18%' },
  { ...CatalogTable.columns.createSystemColumn(), width: '13%' },
  { ...CatalogTable.columns.createSpecTypeColumn(), width: '8%' },
  { ...CatalogTable.columns.createSpecLifecycleColumn(), width: '8%' },
  urlColumn,
  descriptionColumn,
];

/**
 * The catalog index page.
 *
 * Renders `CatalogTable` directly rather than going through
 * `CatalogIndexPage`. That component always emits
 * `CatalogFilterLayout.Filters` as a `<Grid item lg={2})`; our only filter is
 * hidden, so the column renders empty while still reserving a sixth of the
 * width. That empty band is the whole complaint, and no prop suppresses it.
 *
 * This shape was briefly replaced by `CatalogIndexPage` plus CSS to collapse
 * that column, on the strength of a jsdom test in which this page rendered no
 * table. That was wrong twice over: the same harness renders nothing for
 * `CatalogIndexPage` too, so it was measuring the harness, and this page had
 * meanwhile been confirmed working in the browser. The CSS never shipped
 * either. Composing directly is both simpler and the version with real
 * evidence behind it.
 *
 * No `pagination` prop, matching the build that was verified working:
 * `useEntityListProvider` then uses `paginationMode: "none"` and fetches via
 * `getEntities`, which is the `order=asc:metadata.name` request visible in the
 * backend logs.
 *
 * `EntityKindPicker` with `hidden` seeds the query without drawing anything.
 * It returns `null` from render while its `useEntityKindFilter` hook calls
 * `updateFilters({ kind })`. That is required: `useEntityListProvider` returns
 * early without fetching while no filter is active, which is why disabling all
 * eight stock filter extensions left the table empty.
 */
export function AttenivCatalogPage() {
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Atteniv';

  return (
    <EntityListProvider>
      <PageWithHeader title={`${orgName} Catalog`} themeId="home">
        <Content>
          <ContentHeader title="">
            {/* The scaffolder is mounted at /create by page:scaffolder. */}
            <CreateButton title="Create" to="/create" />
            <SupportButton>
              All Atteniv components, APIs and systems.
            </SupportButton>
          </ContentHeader>
          <EntityKindPicker initialFilter="component" hidden />
          <CatalogTable columns={columns} />
        </Content>
      </PageWithHeader>
    </EntityListProvider>
  );
}

/**
 * Registered as a NEW extension id, `page:catalog/atteniv`, with the stock
 * `page:catalog` switched off in app-config.
 *
 * An earlier attempt declared this without a name, so it resolved to
 * `page:catalog` and replaced the stock page by id. That is the documented
 * mechanism and it resolves correctly in `catalogOverride.test.ts`, but it
 * never took effect against the real deployment. Adding an extension and
 * disabling one by config are both already proven here, so this avoids
 * replacement entirely.
 *
 * `routeRef` stays bound to `catalogPlugin.routes.catalogIndex`, the same
 * `rootRouteRef` the stock page used, so routing is unchanged. The sidebar
 * takes this id explicitly in modules/nav/Sidebar.tsx.
 */
const catalogPage = PageBlueprint.make({
  name: 'atteniv',
  params: {
    path: '/catalog',
    title: 'Catalog',
    icon: <CategoryIcon fontSize="inherit" />,
    routeRef: catalogPlugin.routes.catalogIndex,
    loader: async () => <AttenivCatalogPage />,
  },
});

export const catalogModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [catalogPage],
});
